import React from "react";
import call from "./call";
import useQuery, { useGetQueryData, useMutateBody } from "./useQuery";
import { useChannelUpdates } from "./ws/useChannelUpdates";
import type { ManualFailure } from "./useManualFailures";
import { useManualFailures } from "./useManualFailures";
import { comparatorBy } from "diagnostics-lib/comparator";
import useActiveAnalysesStream from "./useActiveAnalysesStream";
import { useSettings } from "./useSettings";
import { useManualFaultEvents } from "./useManualFaultEvents";
import {
  getManualFaultEventEndsAt,
  getManualFaultImpactsByAnalysis,
} from "diagnostics-domain/manualFaultEvent";
import { indexBy } from "diagnostics-lib/iterable";
import usePerformanceIndicators from "./usePerformanceIndicators";
const comparator = comparatorBy("createdAt");

export function buildManualFailureValues({
  manualFaultEvents,
  activeAnalyses,
  performanceIndicators,
}: {
  manualFaultEvents: ReturnType<typeof useManualFaultEvents>;
  activeAnalyses: ReturnType<typeof useActiveAnalysesStream>["activeAnalyses"];
  performanceIndicators: { id: string }[];
}): { [analysisId: string]: { [manualFailureId: string]: ManualFailureValue } } {
  const result: { [analysisId: string]: { [manualFailureId: string]: ManualFailureValue } } = {};

  const activeAnalyseDates = activeAnalyses.map(a => a.date);
  const analysesByDate = indexBy(activeAnalyses, "date");

  for (const event of manualFaultEvents) {
    const endsAt = getManualFaultEventEndsAt(
      event.startsAt,
      event.closedAt,
      activeAnalyses.map(a => new Date(a.date)),
    );

    const impactsOverPeriod = getManualFaultImpactsByAnalysis({
      manualFaultEvent: {
        failureName: event.metadata.manualFault.name,
        windows: event.windows,
        impacts: event.impacts,
        endsAt,
      },
      activeAnalyseDatesPeriod: activeAnalyseDates,
      performanceIndicators,
    });

    for (const impact of impactsOverPeriod) {
      const analysisId = analysesByDate.get(impact.date)?.id;
      if (!analysisId) continue;

      const byManualFailureId = (result[analysisId] ??= {});
      byManualFailureId[event.manualFaultId] = {
        manualFailureId: event.manualFaultId,
        manualFailureName: event.metadata.manualFault.name,
        impacts: impact.real,
        reparability: event.reparability,
      };
    }
  }

  return result;
}

const PATH = "/projects/:projectId(uuid)/manual-failure-values" as const;

const useManualFailureValues = (projectId: string, enabled: boolean) => {
  const rawValues = useQuery(PATH, {
    routeParams: { projectId },
    disabled: !enabled,
  })?.body;

  const manualFailureValues = React.useMemo(() => rawValues ?? [], [rawValues]);

  const getCached = useGetQueryData(PATH, {
    routeParams: { projectId },
  });
  const readItems = React.useCallback(() => getCached()?.body, [getCached]);
  const fetchUpdatedItems = React.useCallback(
    async (since: string) => {
      const { manualFailureValues, deletedManualFailureValueIds } = (
        await call("GET", "/projects/:projectId(uuid)/manual-failure-values/updates/:since(date)", {
          routeParams: { projectId, since: new Date(since) },
        })
      ).body;
      return [manualFailureValues, deletedManualFailureValueIds] as const;
    },
    [projectId],
  );

  const mutateItems = useMutateBody("/projects/:projectId(uuid)/manual-failure-values", {
    routeParams: { projectId },
  });

  useChannelUpdates(
    { key: "manual_failure_values", projectId },
    {
      fetchUpdatedItems,
      mutateItems,
      readItems,
    },
    enabled,
  );

  return React.useMemo(() => manualFailureValues.sort(comparator), [manualFailureValues]);
};

export const useManualFailureValueAnalysisIdIndex = (projectId: string, enabled: boolean) => {
  const manualFailureValues = useManualFailureValues(projectId, enabled);

  const analysisIdIndexedManualFailureValues = React.useMemo(
    () =>
      manualFailureValues.reduce<Record<string, RawManualFailureValue[]>>(
        (acc, manualFailureValue) => {
          const analysisId = manualFailureValue.analysisId;
          if (acc[analysisId]) acc[analysisId].push(manualFailureValue);
          else acc[analysisId] = [manualFailureValue];

          return acc;
        },
        {},
      ),
    [manualFailureValues],
  );

  return analysisIdIndexedManualFailureValues;
};

export interface ManualFailureValue {
  manualFailureId: string;
  manualFailureName: string;
  impacts: Record<string /* quantityId */, number /* value */>;
  reparability: Reparability;
}

export function computeImpacts(impacts: ManualFailureImpacts) {
  return impacts.reduce<Record<string /* quantityId */, number>>((acc, impact) => {
    acc[impact.quantityId] = impact.value;
    return acc;
  }, {});
}

export const useIndexedManualFailureValues = (projectId: string) => {
  const settings = useSettings();
  const performanceIndicators = usePerformanceIndicators(projectId);
  const { activeAnalyses } = useActiveAnalysesStream(projectId);
  const rawManualFailures = useManualFailures(projectId);
  const rawManualFailureValues = useManualFailureValues(
    projectId,
    !settings.MANUAL_EVENTS_DEV_MODE_ENABLED,
  );

  const manualFaultEvents = useManualFaultEvents(
    projectId,
    settings.MANUAL_EVENTS_DEV_MODE_ENABLED,
  );

  const manualFailureIndex = React.useMemo(
    () =>
      rawManualFailures.reduce<{ [manualFailureId: string]: ManualFailure }>(
        (acc, manualFailure) => {
          acc[manualFailure.id] = manualFailure;
          return acc;
        },
        {},
      ),
    [rawManualFailures],
  );

  const analysisIdIndexedManualFailureValues = React.useMemo(
    () =>
      rawManualFailureValues.reduce<{
        [analysisId: string]: RawManualFailureValue[];
      }>((acc, manualFailureValue) => {
        if (acc[manualFailureValue.analysisId] === undefined)
          acc[manualFailureValue.analysisId] = [];

        acc[manualFailureValue.analysisId]!.push(manualFailureValue);
        return acc;
      }, {}),
    [rawManualFailureValues],
  );

  return React.useMemo(() => {
    if (settings.MANUAL_EVENTS_DEV_MODE_ENABLED) {
      return buildManualFailureValues({
        manualFaultEvents,
        activeAnalyses,
        performanceIndicators,
      });
    }

    const failureValuesByAnalysisId: {
      [analysisId: string]: { [manualFailureId: string]: ManualFailureValue };
    } = {};

    for (const analysisId in analysisIdIndexedManualFailureValues) {
      const manualFailureValues = analysisIdIndexedManualFailureValues[analysisId];
      if (manualFailureValues === undefined) continue;

      const indexedManualFailureValues: { [manualFailureId: string]: ManualFailureValue } = {};
      for (const mfv of manualFailureValues) {
        const manualFailure = manualFailureIndex[mfv.manualFailureId];
        if (manualFailure === undefined) continue;

        indexedManualFailureValues[mfv.manualFailureId] = {
          manualFailureId: mfv.manualFailureId,
          manualFailureName: manualFailure.name,
          impacts: computeImpacts(mfv.manualFailureImpacts),
          reparability: mfv.reparability,
        };
      }

      failureValuesByAnalysisId[analysisId] = indexedManualFailureValues;
    }

    return failureValuesByAnalysisId;
  }, [
    activeAnalyses,
    analysisIdIndexedManualFailureValues,
    settings.MANUAL_EVENTS_DEV_MODE_ENABLED,
    manualFailureIndex,
    manualFaultEvents,
    performanceIndicators,
  ]);
};

export type RawManualFailureValue = ReturnType<typeof useManualFailureValues>[number];
export type ManualFailureImpacts = RawManualFailureValue["manualFailureImpacts"];
export type Reparability = RawManualFailureValue["reparability"];

export type IndexedManualFailureValues = ReturnType<typeof useIndexedManualFailureValues>;
